import { resolveCachedImageSrc } from "../utils/images";
import { createIcon } from "../utils/icons";

export interface PhotoPicker {
    element: HTMLElement;
    getFile: () => File | null;
    getCleared: () => boolean;
    setImageSrc: (src: string | null) => void;
}

export interface PhotoPickerOptions {
    existingImageSrc?: string | null;
    onImageChange?: () => void;
}

export function createPhotoPicker(options: PhotoPickerOptions = {}): PhotoPicker {
    const { onImageChange } = options;
    let selectedFile: File | null = null;
    let previewUrl: string | null = null;
    let cleared = false;
    let lastImageSrc: string | null = null;
    let lastWasExisting = false;

    const root = document.createElement("div");
    root.className = "photo-attach";

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "photo-attach-trigger";
    trigger.setAttribute("aria-label", "Photo preview");

    const previewImg = document.createElement("img");
    previewImg.className = "photo-attach-img hidden";
    previewImg.alt = "";

    const placeholder = document.createElement("span");
    placeholder.className = "photo-attach-placeholder";
    placeholder.appendChild(createIcon("photo_camera"));

    trigger.appendChild(previewImg);
    trigger.appendChild(placeholder);

    const actions = document.createElement("div");
    actions.className = "photo-attach-actions";

    const takeBtn = document.createElement("button");
    takeBtn.type = "button";
    takeBtn.className = "photo-attach-action";
    takeBtn.setAttribute("aria-label", "Take photo");
    takeBtn.appendChild(createIcon("photo_camera"));
    const takeLabel = document.createElement("span");
    takeLabel.textContent = "Take";
    takeBtn.appendChild(takeLabel);

    const chooseBtn = document.createElement("button");
    chooseBtn.type = "button";
    chooseBtn.className = "photo-attach-action";
    chooseBtn.setAttribute("aria-label", "Choose photo");
    chooseBtn.appendChild(createIcon("photo_library"));
    const chooseLabel = document.createElement("span");
    chooseLabel.textContent = "Choose";
    chooseBtn.appendChild(chooseLabel);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "photo-attach-action photo-attach-action--danger";
    removeBtn.setAttribute("aria-label", "Remove photo");
    removeBtn.appendChild(createIcon("delete"));
    const removeLabel = document.createElement("span");
    removeLabel.textContent = "Remove";
    removeBtn.appendChild(removeLabel);

    const restoreBtn = document.createElement("button");
    restoreBtn.type = "button";
    restoreBtn.className = "photo-attach-action photo-attach-action--restore hidden";
    restoreBtn.setAttribute("aria-label", "Restore photo");
    restoreBtn.appendChild(createIcon("undo"));
    const restoreLabel = document.createElement("span");
    restoreLabel.textContent = "Restore";
    restoreBtn.appendChild(restoreLabel);

    actions.appendChild(takeBtn);
    actions.appendChild(chooseBtn);
    actions.appendChild(removeBtn);
    actions.appendChild(restoreBtn);

    const cameraInput = document.createElement("input");
    cameraInput.type = "file";
    cameraInput.accept = "image/*";
    cameraInput.setAttribute("capture", "environment");
    cameraInput.hidden = true;

    const galleryInput = document.createElement("input");
    galleryInput.type = "file";
    galleryInput.accept = "image/*";
    galleryInput.hidden = true;

    root.appendChild(trigger);
    root.appendChild(actions);
    root.appendChild(cameraInput);
    root.appendChild(galleryInput);

    function showImage(src: string): void {
        previewImg.src = src;
        previewImg.classList.remove("hidden");
        placeholder.classList.add("hidden");
        trigger.classList.add("has-image");
        trigger.setAttribute("aria-label", "Current photo");
        removeBtn.classList.remove("hidden");
        restoreBtn.classList.add("hidden");
    }

    function clearImage(): void {
        previewImg.classList.add("hidden");
        previewImg.removeAttribute("src");
        placeholder.classList.remove("hidden");
        trigger.classList.remove("has-image");
        trigger.setAttribute("aria-label", "Add photo");
        removeBtn.classList.add("hidden");
        restoreBtn.classList.add("hidden");
    }

    function setFile(file: File | null): void {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrl = null;
        }
        selectedFile = file;
        cleared = false;
        if (file) {
            previewUrl = URL.createObjectURL(file);
            lastImageSrc = previewUrl;
            lastWasExisting = false;
            showImage(previewUrl);
        } else if (lastImageSrc) {
            lastWasExisting = true;
            showImage(lastImageSrc);
        } else {
            clearImage();
        }
        onImageChange?.();
    }

    function handleInputChange(input: HTMLInputElement): void {
        const file = input.files?.[0] ?? null;
        input.value = "";
        setFile(file);
    }

    takeBtn.addEventListener("click", () => cameraInput.click());
    chooseBtn.addEventListener("click", () => galleryInput.click());
    removeBtn.addEventListener("click", () => {
        cleared = true;
        previewImg.classList.add("hidden");
        previewImg.removeAttribute("src");
        placeholder.classList.remove("hidden");
        trigger.classList.remove("has-image");
        trigger.setAttribute("aria-label", "Add photo");
        removeBtn.classList.add("hidden");
        restoreBtn.classList.remove("hidden");
        onImageChange?.();
    });
    restoreBtn.addEventListener("click", () => {
        cleared = false;
        if (lastImageSrc) {
            showImage(lastImageSrc);
        }
        onImageChange?.();
    });
    cameraInput.addEventListener("change", () => handleInputChange(cameraInput));
    galleryInput.addEventListener("change", () => handleInputChange(galleryInput));

    function setImageSrc(src: string | null): void {
        selectedFile = null;
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrl = null;
        }
        cleared = false;

        if (!src) {
            clearImage();
            lastImageSrc = null;
            lastWasExisting = false;
            onImageChange?.();
            return;
        }

        if (src.startsWith("blob:")) {
            lastImageSrc = src;
            lastWasExisting = true;
            showImage(src);
            onImageChange?.();
        } else {
            void resolveCachedImageSrc(src).then((resolved) => {
                if (resolved) {
                    lastImageSrc = resolved;
                    lastWasExisting = true;
                    showImage(resolved);
                } else {
                    clearImage();
                    lastImageSrc = null;
                    lastWasExisting = false;
                }
                onImageChange?.();
            });
        }
    }

    if (options.existingImageSrc) {
        setImageSrc(options.existingImageSrc);
    } else {
        removeBtn.classList.add("hidden");
        restoreBtn.classList.add("hidden");
    }

    return {
        element: root,
        getFile: () => selectedFile,
        getCleared: () => cleared,
        setImageSrc,
    };
}
